// Liste des dossiers d'étude accessibles (sites autorisés uniquement).
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, FileSearch, FlaskConical, Plus, Search, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, depuis, pluriel } from '../../lib/format.ts';
import { LIBELLES_MOTIF, type MotifClassement } from '@shared/classement.ts';
import { Alerte, Badge, Bouton, Chargement, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutDossier, STATUTS_DOSSIER } from '../../components/statuts.tsx';
import { BoutonChargerDemo } from '../../components/BoutonChargerDemo.tsx';

interface DossierResume {
  id: string; reference: string; title: string; status: string; demo: boolean; updated_at: string; site_id: string; site_name: string;
  client_name: string | null; nb_pieces: number; nb_a_traiter: number; nb_ecarts: number; nb_questions: number;
  closed_at: string | null; closed_reason: string | null;
}

export function PageEtudes() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const pourConception = params.get('pour') === 'conception';
  const statut = params.get('statut') ?? '';
  // Actifs (par défaut) ou classés sans suite.
  const classes = params.get('classement') === 'classes';
  const [filtre, setFiltre] = useState('');
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const requete = new URLSearchParams();
  if (statut) requete.set('statut', statut);
  if (classes) requete.set('classement', 'classes');
  const q = useQuery({ queryKey: ['dossiers', statut, classes], queryFn: () => api.get<DossierResume[]>(`/api/dossiers${requete.toString() ? `?${requete}` : ''}`) });
  const voir = (c: boolean) => { const p = new URLSearchParams(params); if (c) p.set('classement', 'classes'); else p.delete('classement'); setParams(p, { replace: true }); };
  const liste = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return (q.data ?? []).filter((x) => !f || [x.title, x.reference, x.site_name, x.client_name].some((v) => v?.toLowerCase().includes(f)));
  }, [q.data, filtre]);
  const aDemo = (q.data ?? []).some((x) => x.demo);

  const supprimerDemo = async () => {
    if (!(await confirmer('Supprimer les données de démonstration', 'Le site, le client, le dossier, les pièces et le devis fictifs de démonstration seront supprimés pour toute l’organisation. Vos données réelles ne sont pas touchées.', 'Supprimer la démonstration'))) return;
    try {
      await api.del('/api/demo');
      await qc.invalidateQueries();
      notifier('Données de démonstration supprimées.');
    } catch (e) { notifier(messageErreur(e), true); }
  };

  return (
    <div className="pile-l">
      {element}
      <EntetePage titre="Études et devis" sousTitre="Analyse des pièces d’un dossier avant devis : exigences sourcées, écarts, questions, périmètre."
        actions={peut('dossier.ecrire') && <LienBouton to="/etudes/nouveau" variante="principal" icone={<Plus aria-hidden />}>Nouveau dossier</LienBouton>} />
      {pourConception && (
        <Alerte ton="info" titre="Concevoir et chiffrer">
          Choisissez le dossier concerné : son onglet Conception s’ouvrira directement. Pour un nouveau projet, créez d’abord le dossier.
        </Alerte>
      )}
      <div className="segment et-segment et-segment-classement" role="group" aria-label="Dossiers affichés">
        <button type="button" aria-pressed={!classes} onClick={() => voir(false)}>Actifs</button>
        <button type="button" aria-pressed={classes} onClick={() => voir(true)}><Archive size={16} aria-hidden /> Classés sans suite</button>
      </div>
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && q.data.length === 0 && classes && !statut && (
        <Panneau>
          <EtatVide icone={<Archive />} titre="Aucun dossier classé sans suite"
            texte="Un dossier abandonné (client qui renonce, marché perdu, démonstration terminée) se classe depuis son menu « Plus ». Il reste consultable ici et peut être rouvert." />
        </Panneau>
      )}
      {q.data && q.data.length === 0 && !statut && !classes && (
        <Panneau>
          <EtatVide icone={<FileSearch />} titre="Aucun dossier d’étude accessible"
            texte="Créez un dossier et déposez ses pièces (demande du client, CCTP, plans, DPGF), ou ouvrez l’exemple de démonstration pour découvrir l’analyse."
            action={<div className="groupe-btn">{peut('dossier.ecrire') && <LienBouton to="/etudes/nouveau" variante="principal">Créer un dossier</LienBouton>}<BoutonChargerDemo /></div>} />
        </Panneau>
      )}
      {q.data && (q.data.length > 0 || statut) && (
        <Panneau serre titre={pluriel(liste.length, 'dossier')} actions={
          <div className="et-filtres-liste">
            <label className="champ"><span className="visuellement-cache">Statut</span>
              <select className="saisie" value={statut} onChange={(e) => { const p = new URLSearchParams(params); if (e.target.value) p.set('statut', e.target.value); else p.delete('statut'); setParams(p, { replace: true }); }}>
                <option value="">Tous les statuts</option>
                {Object.entries(STATUTS_DOSSIER).map(([k, v]) => <option key={k} value={k}>{v.libelle}</option>)}
              </select></label>
            <label className="rangee et-recherche-liste">
              <Search size={18} aria-hidden className="discret" />
              <span className="visuellement-cache">Filtrer les dossiers</span>
              <input className="saisie" placeholder="Référence, titre, site, client" value={filtre} onChange={(e) => setFiltre(e.target.value)} />
            </label>
          </div>}>
          <div className="liste">
            {liste.map((x) => (
              <Link key={x.id} to={pourConception ? `/etudes/${x.id}/conception` : `/etudes/${x.id}`} className="liste-element et-dossier-ligne">
                <FileSearch aria-hidden className="discret masque-etroit" />
                <span className="liste-element-corps">
                  <span className="liste-element-titre">{x.title}</span>
                  <span className="petit discret"><span className="mono">{x.reference}</span> — {[x.site_name, x.client_name].filter(Boolean).join(' — ')}</span>
                  <span className="et-dossier-chiffres tres-petit secondaire">
                    <span>{pluriel(x.nb_pieces, 'pièce')}</span>
                    {x.nb_a_traiter > 0 && <span>{pluriel(x.nb_a_traiter, 'exigence à traiter', 'exigences à traiter')}</span>}
                    {x.nb_ecarts > 0 && <span>{pluriel(x.nb_ecarts, 'écart ouvert', 'écarts ouverts')}</span>}
                    {x.nb_questions > 0 && <span>{pluriel(x.nb_questions, 'question à poser', 'questions à poser')}</span>}
                  </span>
                </span>
                <span className="et-dossier-badges">
                  {x.closed_at
                    ? <Badge ton="neutre" icone={<Archive size={14} aria-hidden />} titre={`Classé le ${date(x.closed_at)}`}>{LIBELLES_MOTIF[x.closed_reason as MotifClassement] ?? 'Classé sans suite'}</Badge>
                    : <BadgeStatutDossier statut={x.status} />}
                  {x.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                </span>
                <span className="tres-petit discret expert-seulement masque-etroit">{depuis(x.updated_at)}</span>
              </Link>
            ))}
            {liste.length === 0 && <EtatVide titre="Aucun dossier ne correspond" texte="Modifiez le filtre ou le statut." />}
          </div>
        </Panneau>
      )}
      {aDemo && peut('admin.configuration') && (
        <div className="groupe-btn"><Bouton variante="fantome" className="et-btn-souple" icone={<Trash2 aria-hidden />} onClick={supprimerDemo}>Supprimer les données de démonstration</Bouton></div>
      )}
    </div>
  );
}
